import { supabase } from '../supabaseClient';

class RealtimeNotificationManager {
  constructor(userId, userToken, userRole) {
    this.userId = userId;
    this.token = userToken;
    this.userRole = userRole; // 'admin' or 'merchant'
    this.channels = [];
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners = [];
    this.isConnected = false;
  }

  async init() {
    try {
      console.log('🔔 Initializing realtime notifications for:', this.userRole, this.userId);
      
      // Set auth token for authenticated requests
      if (this.token) {
        await supabase.auth.setSession({ access_token: this.token, refresh_token: '' });
      }
      
      // Subscribe to notifications based on user role
      await this.subscribeToNotifications();
      
      // Load existing notifications
      await this.loadExistingNotifications();
      
      this.isConnected = true;
      console.log('✅ Realtime notifications initialized');
    } catch (error) {
      console.error('❌ Error initializing realtime notifications:', error);
    }
  }

  async subscribeToNotifications() {
    const tableName = this.userRole === 'merchant' ? 'merchant_notification_logs' : 'admin_notification_logs';
    const filterField = this.userRole === 'merchant' ? 'merchant_user_id' : 'admin_user_id';
    
    const channel = supabase
      .channel(`${this.userRole}-notifications-${this.userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: tableName,
          filter: `${filterField}=eq.${this.userId}`
        },
        (payload) => {
          console.log('📨 New notification received:', payload);
          this.handleNewNotification(payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: tableName,
          filter: `${filterField}=eq.${this.userId}`
        },
        (payload) => {
          console.log('📝 Notification updated:', payload);
          this.handleNotificationUpdate(payload.new);
        }
      )
      .subscribe();

    this.channels.push(channel);
    console.log(`📡 Subscribed to ${this.userRole} notifications`);
  }

  async loadExistingNotifications() {
    try {
      // Use the new API endpoints instead of direct Supabase queries
      const endpoint = this.userRole === 'merchant' ? '/api/merchant/notifications' : '/api/admin/notifications';
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${endpoint}?limit=50&offset=0`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Error loading notifications:', response.status, response.statusText);
        return;
      }

      const result = await response.json();
      this.notifications = result.data || [];
      this.unreadCount = this.notifications.filter(n => !n.read_at).length;
      
      console.log(`📋 Loaded ${this.notifications.length} existing notifications via API`);
      this.notifyListeners('notifications_loaded', { notifications: this.notifications, unreadCount: this.unreadCount });
    } catch (error) {
      console.error('Error loading existing notifications:', error);
    }
  }

  handleNewNotification(notification) {
    // Add to beginning of notifications array
    this.notifications.unshift(notification);
    
    // Update unread count
    if (!notification.read_at) {
      this.unreadCount++;
    }
    
    // Show browser notification if permission granted
    this.showBrowserNotification(notification);
    
    // Notify listeners
    this.notifyListeners('new_notification', { 
      notification, 
      notifications: this.notifications, 
      unreadCount: this.unreadCount 
    });
    
    console.log('🔔 New notification processed:', notification.title);
  }

  handleNotificationUpdate(notification) {
    // Update existing notification in array
    const index = this.notifications.findIndex(n => n.id === notification.id);
    if (index !== -1) {
      this.notifications[index] = notification;
      
      // Update unread count
      this.unreadCount = this.notifications.filter(n => !n.read_at).length;
      
      // Notify listeners
      this.notifyListeners('notification_updated', { 
        notification, 
        notifications: this.notifications, 
        unreadCount: this.unreadCount 
      });
    }
  }

  showBrowserNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
        body: notification.body,
        icon: '/icon-192x192.png',
        badge: '/badge-72x72.png',
        tag: `${this.userRole}-${notification.id}`,
        data: notification,
        requireInteraction: true
      });

      // Handle notification click
      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
        this.notifyListeners('notification_clicked', { notification });
      };
    }
  }

  async markAsRead(notificationId) {
    try {
      const endpoint = this.userRole === 'merchant' ? '/api/merchant/notifications' : '/api/admin/notifications';
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${endpoint}/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Error marking notification as read:', response.status, response.statusText);
        return false;
      }

      // Update local state
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.read_at = new Date().toISOString();
        this.unreadCount = this.notifications.filter(n => !n.read_at).length;
        
        this.notifyListeners('notification_read', { 
          notification, 
          notifications: this.notifications, 
          unreadCount: this.unreadCount 
        });
      }

      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  async markAllAsRead() {
    try {
      const endpoint = this.userRole === 'merchant' ? '/api/merchant/notifications' : '/api/admin/notifications';
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${endpoint}/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Error marking all notifications as read:', response.status, response.statusText);
        return false;
      }

      // Update local state
      this.notifications.forEach(n => {
        if (!n.read_at) {
          n.read_at = new Date().toISOString();
        }
      });
      this.unreadCount = 0;
      
      this.notifyListeners('all_notifications_read', { 
        notifications: this.notifications, 
        unreadCount: this.unreadCount 
      });

      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  // Event listener system
  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  notifyListeners(event, data) {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  getNotifications() {
    return this.notifications;
  }

  getUnreadCount() {
    return this.unreadCount;
  }

  disconnect() {
    // Unsubscribe from all channels
    this.channels.forEach(channel => {
      supabase.removeChannel(channel);
    });
    this.channels = [];
    this.listeners = [];
    this.isConnected = false;
    console.log('🔌 Realtime notifications disconnected');
    console.log('🔍 Disconnect reason: User logged out or connection lost');
  }
}

// Singleton instances
let realtimeManager = null;

export function initializeRealtimeNotifications(userId, userToken, userRole) {
  try {
    console.log('🚀 Initializing realtime notifications:', { userId, userRole, hasToken: !!userToken });
    
    if (realtimeManager) {
      console.log('🔄 Disconnecting existing realtime manager');
      realtimeManager.disconnect();
    }
    
    realtimeManager = new RealtimeNotificationManager(userId, userToken, userRole);
    console.log('✅ New realtime manager created');
    return realtimeManager;
  } catch (error) {
    console.warn('⚠️ Failed to initialize realtime notifications:', error);
    return null;
  }
}

export function getRealtimeManager() {
  return realtimeManager;
}

export function disconnectRealtimeNotifications() {
  console.log('🔌 Disconnecting realtime notifications (logout or cleanup)');
  if (realtimeManager) {
    realtimeManager.disconnect();
    realtimeManager = null;
  }
}

export default RealtimeNotificationManager;
